import React, { useCallback, useMemo, useRef, useState } from "react";
import "./../../styles/homePage/patientsList.scss";
import TablePage from "../../components/patientsList/genericTable/TablePage";
import { useTranslation } from "react-i18next";
import { TableCard } from "../../common/types/tableCardType";
import { useQuery } from "@tanstack/react-query";
import {
  getDeviceCardsData,
} from "../../common/api/apiCalls";
import { svgs } from "../../common/assats";
import { useNavigate } from "react-router-dom";
import { debounce } from "@mui/material";
import { GenericRecord } from "../../common/types/filterObjType";
import {
  addDevices,
  deleteDevice,
  exportDevices,
  fetchAlldevices,
} from "../../common/api/deviceService";
import {
  organizeDeviceData,
  processFile,
} from "../../common/function/organizeDeviceData";
import { getAllSites } from "../../common/api/siteService";
import Modal from "../../components/Modal/Modal";
import FileUpload, {
  FileUploadRef,
} from "../../components/FileUpload/FileUpload";
import useModal from "../../common/hooks/useModal";
import "./Devices.scss";
import PopUp from "../../components/PatientCharts/PopUp";

import { useAlert } from "../../common/contexts/AlertContext";
import Table from "../../components/patientsList/genericTable/Table";
import Loader from "../../components/Loader/Loader";

const sortKeyArray = [
  "serialNumber",
  "creationDate",
  "firmwareVersionId",
  "hardwareVersionId",
  "patient",
  "site",
  "relatedHCP",
  "status",
  // "batteryLevel",
  "lastSync",
  "errorType",
  "applicationVersion",
];

export enum StatusEnum {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
  ERROR = "Error",
  UNASSIGNED = "Unassigned",
  ON_HOLD = "On Hold",
}

const Devices = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alert = useAlert();

  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>(""); // Debounced value for API
  const [sortKey, setSortKey] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [filters, setFilters] = useState<string[]>([]);

  const getText = (text: string) => t(`deviceList.${text}`);

  // ====== Modal (FileUpload) States ======
  const [isFileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [fileUploadFooterActions, setFileUploadFooterActions] =
    useState<React.ReactNode>(null);
  const [fileUploadContent, setFileUploadContent] =
    useState<React.ReactNode>(null);
  const [fileUploadTitle, setFileUploadTitle] = useState<string>("");
  const [fileUploadDescription, setFileUploadDescription] =
    useState<string>("");
  const [fileUploadContentClass, setFileUploadContentClass] =
    useState<string>("");
  const fileUploadRef = useRef<FileUploadRef>(null);

  // ====== Delete Popup States ======
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<string | null>(null);

  const {
    isOpen: isErrorsModalOpen,
    modalTitle,
    modalDescription,
    modalContent,
    openModal,
    closeModal,
  } = useModal();
  const {
    data: devicesList,
    error: devicesFetchError,
    refetch,
    isLoading: isLoadingDevices,
  } = useQuery({
    queryKey: ["usersData", sortKey, sortOrder, debouncedSearch, filters],
    queryFn: () =>
      fetchAlldevices(sortKey, sortOrder, debouncedSearch, filters),
  });

  // Second query, dependent on the first one
  const {
    data: filtersData,
    error: siteDataError,
    isLoading: isCenterDataLoading,
  } = useQuery({
    queryKey: ["centerData"],
    queryFn: getAllSites,
  });

  const {
    data: cardsData,
    error: userCardsError,
    isLoading: isCardsDataLoading,
  } = useQuery({
    queryKey: ["cards"],
    queryFn: getDeviceCardsData,
  });

  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearch(value);
    }, 300), // 300ms delay
    []
  );

  const handleSearchChange = (str: string) => {
    setSearch(str); // Instant feedback for the user
    debouncedSetSearch(str); // Delayed API request
  };

  const cards: TableCard[] = useMemo(
    () => [
      {
        headline: getText("cards.devices"),
        icon: svgs.cards.DevicesIcon,
        value: isCardsDataLoading ? 0 : cardsData[0] || 0,
        action: () => {
          console.log("click");
        },
      },
      {
        headline: getText("cards.centers"),
        icon: svgs.cards.Centers,
        value: isCardsDataLoading ? 0 : cardsData[1] || 0,
        action: () => {
          console.log("click");
        },
      },
      {
        headline: getText("cards.errors"),
        icon: svgs.cards.ErrorIcon,
        value: isCardsDataLoading ? 0 : cardsData[2] || 0,
        action: () => {
          console.log("click");
        },
      },
    ],
    [cardsData]
  );

  function getFilters(data: { id: string; SiteName: string }[]) {
    if (!data || data.length === 0 || !Array.isArray(data)) {
      return {};
    }
    let filterObj: GenericRecord<string> = {
      center: data.map((val) => {
        return { name: val.SiteName, value: val.SiteName };
      }),
      status: [
        { name: StatusEnum.ACTIVE, value: StatusEnum.ACTIVE },
        { name: StatusEnum.INACTIVE, value: StatusEnum.INACTIVE },
        { name: StatusEnum.ERROR, value: StatusEnum.ERROR },
        { name: StatusEnum.ON_HOLD, value: StatusEnum.ON_HOLD },
        { name: StatusEnum.UNASSIGNED, value: StatusEnum.UNASSIGNED },
      ],
    };
    return filterObj;
  }
  function handleRowClick(index: number) {
    navigate("DeviceProfile", {
      state: { user: devicesList[index] },
    });
  }

  const handleFileUpload = async (files: File[]): Promise<void> => {
    if (!files.length) return;

    try {
      const serialNumbers = await processFile(files[0]); // Process the uploaded file

      if (serialNumbers.length === 0) {
        alert("The file contains no serial numbers.", "error");
        return;
      }
      await addDevices(serialNumbers); // Send all serial numbers in a single API call
      alert("Devices added successfully!", "msg");
      setFileUploadModalOpen(false);
      refetch();
    } catch (error) {
      console.error("Error uploading devices:", error);
      alert("Failed to upload devices. Please check the file format.", "error");
    }
  };

  // ====== Handle File Upload Modal ======
  const handleFileUploadModal = () => {
    setFileUploadContent(
      <FileUpload
        ref={fileUploadRef}
        onFilesUploaded={handleFileUpload}
        showFirmwareFields={false}
        templateUrl="/templates/template.xlsx"
        templateDescription={getText("templateDescription")}
      />
    );

    setFileUploadTitle(t("File Upload"));
    setFileUploadDescription(t("Add your documents here"));
    setFileUploadContentClass("modal-step-file-upload");

    setFileUploadFooterActions(
      <div className="modal-footer-actions">
        <button
          className="cancel-button"
          onClick={() => setFileUploadModalOpen(false)}
        >
          {t("Cancel")}
        </button>
        <button
          className="upload-button"
          onClick={async () => {
            console.log("uploading");
          }}
        >
          {t("Upload")}
        </button>
      </div>
    );

    setFileUploadModalOpen(true);
  };

  const handleExport = async () => {
    try {
      const blob = await exportDevices({
        sortKey,
        sortOrder,
        search: debouncedSearch,
        filters,
      });
      // Generate a downloadable URL and trigger the download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Devices_List.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url); // Cleanup URL
    } catch (error) {
      console.error("Error exporting devices:", error);
      alert("Failed to export devices. Please try again.", "error");
    }
  };

  const handleDeleteClick = (index: number) => {
    const device = devicesList[index];
    if (device.status === "Unassigned") {
      const deviceSerialNumber = device.serialNumber;
      setDeviceToDelete(deviceSerialNumber);
      setShowDeletePopup(true);
    } else {
      alert(
        "This device cannot be deleted because its status is not 'Unassigned'.",
        "error"
      );
    }
  };

  const confirmDelete = async () => {
    if (deviceToDelete) {
      try {
        const response = await deleteDevice(deviceToDelete);
        if (response.success) {
          alert("Device deleted successfully!", "msg");
          refetch();
        }
      } catch (error) {
        console.error("Error deleting device:", error);
      } finally {
        setDeviceToDelete(null);
        setShowDeletePopup(false);
      }
    }
  };

  const cancelDelete = () => {
    setDeviceToDelete(null);
    setShowDeletePopup(false);
  };

  const handleOpenErrorsModal = (errors: any[], description = null) => {
    if (errors && errors.length > 0) {
      openModal(
        getText("errorTable.errorLog"),
        description ? description : "",
        <div className="device-errors-table-container">
          <div className="device-errors-table-wrapper">
            <Table
              trashAction={() => {}}
              setFilters={() => {}}
              tableData={errors.map((error) => ({
                error: error.errorline,
                timestamp: new Date(error.timestamp).toLocaleString(),
              }))}
              sortKeysArray={[
                { name: getText("errorTable.errorname"), val: "error" },
                { name: getText("errorTable.errorDate"), val: "timestamp" },
              ]}
              showCheckbox={false}
            />
          </div>
        </div>
      );
    }
  };
  if (isCenterDataLoading || isCardsDataLoading) {
    return <Loader text="loading..." />;
  }
  console.log("filtersData:", getFilters(filtersData));
  return (
    <>
      <TablePage
        filterObj={filtersData ? getFilters(filtersData) : {}}
        tableHeadline={getText(`deviceManagement`)}
        headerHeadline={getText("deviceManagement")}
        cards={cards}
        trashAction={() => {}}
        filters={filters}
        setFilters={setFilters}
        searchValue={search}
        setSearchValue={handleSearchChange}
        searchPlaceholder={getText(
          "filter.Search_by_MAC_Related_Patient_Center_Related_HCP"
        )}
        buttonText={getText("addDevice")}
        buttonAction={handleFileUploadModal}
        setSortKey={setSortKey}
        sortKey={sortKey}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        tableData={
          isLoadingDevices
            ? []
            : organizeDeviceData(devicesList).map((device) => ({
                ...device,
                // batteryLevel: <BatteryLevelCell level={device.batteryLevel} />,
                errors: (
                  <button
                    className={`devices-link-button ${
                      device.errors && device.errors.length > 0
                        ? "has-errors"
                        : ""
                    }`}
                    onClick={() => handleOpenErrorsModal(device.errors)}
                  >
                    {device.errors && device.errors.length > 0 && (
                      <svgs.icons.attention />
                    )}
                  </button>
                ),
              }))
        }
        sortKeysArray={sortKeyArray.map((value) => {
          return { name: getText(`sort.${value}`), val: value };
        })}
        rowClick={handleRowClick}
        subButtonText={getText("export")}
        subAction={handleExport}
        EndRowIcon={svgs.icons.Eye}
        DeleteRowIcon={svgs.icons.deleteIcone}
        deleteClick={handleDeleteClick}
      />
      {/* File Upload Modal */}
      <Modal
        isOpen={isFileUploadModalOpen}
        title={fileUploadTitle}
        description={fileUploadDescription}
        content={fileUploadContent}
        onClose={() => setFileUploadModalOpen(false)}
        footerActions={fileUploadFooterActions}
        contentClass={fileUploadContentClass}
      />
      {/* Errors Modal */}
      <Modal
        isOpen={isErrorsModalOpen}
        content={modalContent}
        onClose={closeModal}
      />
      {/* Confirmation Popup for Delete */}
      {showDeletePopup && (
        <PopUp
          title={getText("popUp.title")}
          message={getText("popUp.message")}
          open={showDeletePopup}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          confirmText={getText("popUp.delete")}
        />
      )}
    </>
  );
};

export default Devices;
